import { isValidEmail, normalizePostalCodeValue } from './validators';

const DEFAULT_RESOLVED_ADDRESS_FORM = {
  addressElements: [],
  lookupField: 'address1',
  requiredFields: [],
  postalCodeRegex: null,
  verifyAfterAutocomplete: false,
  wrapAddress1ToAddress2: false
};

const OPTIONAL_FIELDS = new Set(['address2', 'email']);

const isBlank = (value) => {
  if (value === null || value === undefined) {
    return true;
  }

  return String(value).trim() === '';
};

const resolveAddressForm = (addressForm = null) => {
  if (addressForm && Array.isArray(addressForm.addressElements)) {
    return {
      ...DEFAULT_RESOLVED_ADDRESS_FORM,
      ...addressForm
    };
  }

  return DEFAULT_RESOLVED_ADDRESS_FORM;
};

export const getRequiredFieldsFromAddressForm = (addressForm = null) => {
  const resolvedAddressForm = resolveAddressForm(addressForm);
  const configRequired = resolvedAddressForm?.requiredFields;
  const formFields = resolvedAddressForm?.addressElements?.map((field) => field.field) || [];
  const formFieldSet = new Set(formFields);
  const normalizeRequired = (fields) => {
    const seen = new Set();
    return fields.filter((field) => {
      if (!formFieldSet.has(field) || seen.has(field)) {
        return false;
      }
      seen.add(field);
      return true;
    });
  };

  if (Array.isArray(configRequired) && configRequired.length > 0) {
    return normalizeRequired(configRequired);
  }

  return normalizeRequired(formFields.filter((field) => !OPTIONAL_FIELDS.has(field)));
};

export const getMissingRequiredFields = (addressForm = null, formData = {}) => {
  const requiredFields = getRequiredFieldsFromAddressForm(addressForm);
  return requiredFields.filter((field) => isBlank(formData?.[field]));
};

export const shouldVerifyAutocompleteSelection = ({
  addressForm = null,
  formData = {},
  requireValidation = true
}) => {
  if (!requireValidation) {
    return false;
  }

  const resolvedAddressForm = resolveAddressForm(addressForm);

  if (resolvedAddressForm.verifyAfterAutocomplete) {
    return true;
  }

  return getMissingRequiredFields(resolvedAddressForm, formData).length > 0;
};

const getPostalCodeStatus = (formData, requiredFields, addressForm = null) => {
  const resolvedAddressForm = resolveAddressForm(addressForm);
  const hasPostalField = (resolvedAddressForm?.addressElements || []).some((field) => field.field === 'postalCode');

  if (!hasPostalField) {
    return {
      applicable: false,
      required: false,
      value: '',
      isValid: null
    };
  }

  const postalCode = formData?.postalCode || '';
  const value = normalizePostalCodeValue(postalCode);
  const required = requiredFields.includes('postalCode');
  const regex = resolvedAddressForm?.postalCodeRegex;

  if (value === '') {
    return {
      applicable: true,
      required,
      value,
      isValid: required ? false : null
    };
  }

  if (!regex) {
    return {
      applicable: true,
      required,
      value,
      isValid: null
    };
  }

  return {
    applicable: true,
    required,
    value,
    isValid: regex.test(value)
  };
};

const getEmailStatus = (formData, requiredFields, addressForm = null) => {
  const resolvedAddressForm = resolveAddressForm(addressForm);
  const hasEmailField = (resolvedAddressForm?.addressElements || []).some((field) => field.field === 'email');

  if (!hasEmailField) {
    return null;
  }

  const value = String(formData?.email || '').trim();
  const required = requiredFields.includes('email');

  if (value === '') {
    return {
      applicable: true,
      required,
      value,
      isValid: required ? false : null
    };
  }

  return {
    applicable: true,
    required,
    value,
    isValid: isValidEmail(value)
  };
};

export const buildAddressStatus = ({
  addressForm = null,
  formData,
  verificationStatus,
  mode,
  requireValidation = true
}) => {
  const resolvedAddressForm = resolveAddressForm(addressForm);
  const formAvailable = resolvedAddressForm.addressElements.length > 0;
  const requiredFields = getRequiredFieldsFromAddressForm(resolvedAddressForm);
  const missingFields = getMissingRequiredFields(resolvedAddressForm, formData);
  const postalCode = getPostalCodeStatus(formData, requiredFields, resolvedAddressForm);
  const email = getEmailStatus(formData, requiredFields, resolvedAddressForm);

  const hasInvalidPostalCode = postalCode.applicable && postalCode.value !== '' && postalCode.isValid === false;
  const hasInvalidEmail = email?.applicable && email.value !== '' && email.isValid === false;
  const isComplete = formAvailable &&
    missingFields.length === 0 &&
    !hasInvalidPostalCode &&
    !hasInvalidEmail;
  const completeness = isComplete ? 'complete' : 'incomplete';

  return {
    formAvailable,
    verification: verificationStatus,
    ...(formData?.verification?.manuallyEdited ? {
      manualChanges: {
        manuallyEdited: true,
        editedFields: formData.verification.editedFields || []
      }
    } : {}),
    needsValidation: Boolean(requireValidation && mode === 'manual' && verificationStatus !== 'verified'),
    completeness,
    missingFields,
    mode,
    postalCode,
    ...(email ? { email } : {})
  };
};
